var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect, useRef } from 'react';
import format from 'date-fns/format';
import DatePicker from 'react-datepicker';
import DatepickerTextfield from './DatepickerTextfield';
import '../../../assets/styles/datepicker/iva-datepicker.scss';
import { parseInputDate } from '../../AlmeUtilities/dateutils';
import CalendarHeader from './CalendarHeader';
import { CalendarOverlay, DatePickerWrapper } from './SubComponents';
import { Container, ModalWrapper } from '../../AlmeDialog/ModalDialog';
import { observeMutations, onKeyEscape } from '../../AlmeUtilities/dom-utils';
import { callIfFunction } from '../../../utils/FunctionUtils';
const formatDate = (date) => {
    return date ? format(date, 'MM/dd/yyyy') : "";
};
const selectedDaySelector = 'div[tabindex="0"]';
const focusableCalendarNodes = [
    'button',
    'select',
].join(',');
const ariaDayLabel = 'Use keys Arrow left and Arrow right to switch between dates,Use keys Arrow up and Arrow down to switch between weeks,Use keys Page Up and Page Down to switch between months, Use keys Home and End to switch between years,Press Escape key to close the calendar';
const focusCalendar = (calendar) => {
    const selectedDay = calendar.querySelector(selectedDaySelector);
    if (selectedDay) {
        selectedDay.setAttribute('aria-label', ariaDayLabel);
        selectedDay.focus();
    }
    else {
        const firstFocusableNode = calendar.querySelector(focusableCalendarNodes);
        if (firstFocusableNode) {
            firstFocusableNode.focus();
        }
    }
};
function fixOverlayDatepickerDynamicStyles(popper) {
    const observers = [];
    const disconnectObservers = () => {
        observers.forEach(o => o && o.disconnect());
    };
    const mutationOptions = {
        attributeFilter: ["style"],
    };
    const onAttributeChange = (mutations, observer) => {
        mutations.forEach(m => {
            if (m.attributeName === 'style' && m.target.style.transform !== 'none') {
                m.target.style.transform = 'none';
            }
        });
    };
    observers.push(observeMutations(popper, onAttributeChange, mutationOptions));
    return disconnectObservers;
}
const onClickOutside = (e, dateTriggerId, setIsOpen) => {
    const eventPath = e.composedPath();
    /** Date trigger button has its own handler, so return if trigger button */
    if (eventPath.some(el => el.id === dateTriggerId)) {
        return;
    }
    setIsOpen(false);
};
const useUpdateCalendarAttributesOnOpen = (isOpen, isOverlay, popperClassName, dateTriggerId, setIsOpen) => {
    useEffect(() => {
        let disconnectObservers;
        if (isOpen) {
            const poppers = document.getElementsByClassName(popperClassName);
            if (poppers.length >= 1) {
                const popper = poppers[0];
                onKeyEscape(popper, () => {
                    const trigger = document.getElementById(dateTriggerId);
                    trigger === null || trigger === void 0 ? void 0 : trigger.focus();
                    setIsOpen(false);
                });
                popper.setAttribute('role', 'application');
                popper.setAttribute('aria-label', 'Calendar view date-picker');
                //In Inline datepicker, datepicker opens in overlay mode but popper js adds default modifiers and applies transform
                //remove the transform in calendar popper that are created relative to reference element(Input field)
                if (isOverlay) {
                    disconnectObservers = fixOverlayDatepickerDynamicStyles(popper);
                }
                focusCalendar(popper);
            }
        }
        return () => {
            disconnectObservers && disconnectObservers();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);
};
const DatePickerControl = (_a) => {
    var { id, placeholder, value, minDate, maxDate, isDisabled, error = '', helpText = '', required = false, onChange, onBlur, overlay = false, showCalendar = true, shouldFocus } = _a, props = __rest(_a, ["id", "placeholder", "value", "minDate", "maxDate", "isDisabled", "error", "helpText", "required", "onChange", "onBlur", "overlay", "showCalendar", "shouldFocus"]);
    const [isOpen, setIsOpen] = useState(false);
    const popperClassName = `${id}-popper`;
    const dateTriggerId = `${id}-trigger`;
    const portalId = `${id}-portal`;
    const refOuter = useRef(null);
    useEffect(() => {
        if (props.isFirstOverlay && refOuter.current) {
            const focusBox = document.getElementById(`${id}-input-field`);
            if (focusBox) {
                focusBox.focus();
            }
        }
    }, [props.isFirstOverlay, id]);
    useEffect(() => {
        if (shouldFocus) {
            const focusBox = document.getElementById(`${id}-input-field`);
            if (focusBox) {
                focusBox.focus();
            }
        }
    });
    const dateChange = (date) => {
        callIfFunction(onChange, date);
    };
    const handleTriggerClick = () => {
        setIsOpen(!isOpen);
    };
    const handleClickOutside = (e) => onClickOutside(e, dateTriggerId, setIsOpen);
    const handleInputChange = (e) => {
        const inputValue = e.target.value;
        if (inputValue.length === 0) {
            dateChange(null);
        }
        else if (inputValue.length === 10) {
            const date = parseInputDate(inputValue);
            dateChange(date);
        }
    };
    useUpdateCalendarAttributesOnOpen(isOpen, overlay, popperClassName, dateTriggerId, setIsOpen);
    const overlayPopperProps = {
        strategy: 'static',
    };
    return (React.createElement(DatePickerWrapper, { className: "iva-date-picker-wrapper", ref: refOuter },
        React.createElement(CalendarOverlay, { show: (overlay && isOpen) },
            React.createElement(Container, null,
                React.createElement(ModalWrapper, { id: portalId, className: "iva-date-picker-overlay-portal" }))),
        React.createElement(DatePicker, Object.assign({ id: id, open: isOpen, portalId: (overlay ? portalId : undefined), enableTabLoop: true, selected: value, onChange: (dt) => dateChange(dt), minDate: minDate, maxDate: maxDate, preventOpenOnFocus: true, popperClassName: popperClassName, popperProps: overlay ? overlayPopperProps : undefined, onClickOutside: handleClickOutside, monthsShown: 1, renderCustomHeader: ({ date, changeYear, changeMonth, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled, }) => (React.createElement(CalendarHeader, { date: date, minDate: minDate, maxDate: maxDate, changeYear: changeYear, changeMonth: changeMonth, decreaseMonth: decreaseMonth, increaseMonth: increaseMonth, prevMonthButtonDisabled: prevMonthButtonDisabled, nextMonthButtonDisabled: nextMonthButtonDisabled })), customInput: React.createElement(DatepickerTextfield, { id: id, placeholderLabel: placeholder, inputDate: formatDate(value), triggerClicked: handleTriggerClick, onInputChange: handleInputChange, onInputBlur: onBlur, error: error, helpText: helpText, required: required, isDisabled: isDisabled, showCalendarIcon: showCalendar }) }, props))));
};
export default DatePickerControl;
